/**
 * Révèle — Sélecteur de contenance
 * Référence : docs/01-DESIGN-SYSTEM.md §5.7 et §5.8
 *
 * Trois états de pilule :
 *   disponible → sélectionne la variante (prix, formulaire, URL)
 *   bientot    → ouvre le panneau d'alerte de disponibilité (PAS un état désactivé)
 *   epuise     → annonce la rupture, aucune action
 */

class RvSizeSelector extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.pills = Array.from(this.querySelectorAll('.rv-size__pill'));
    this.notify = this.querySelector('.rv-notify');
    this.notifyForm = this.querySelector('.rv-notify__form');
    this.notifyInput = this.querySelector('.rv-notify__input');
    this.notifyError = this.querySelector('[data-notify-error]');
    this.notifyTitle = this.querySelector('[data-notify-title]');
    this.notifySuccess = this.querySelector('[data-notify-success]');
    this.notifySuccessText = this.querySelector('[data-notify-success-text]');

    const stringsEl = this.querySelector('[data-rv-strings]');
    this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};

    this.pills.forEach((pill) => {
      pill.addEventListener('click', this.onPillClick.bind(this));
    });

    if (this.notifyForm) {
      this.notifyForm.addEventListener('submit', this.onNotifySubmit.bind(this));
      // Validation au blur, jamais à chaque frappe
      this.notifyInput.addEventListener('blur', () => this.validateEmail(true));
      this.notifyInput.addEventListener('input', () => this.clearError());
    }
  }

  onPillClick(event) {
    const pill = event.currentTarget;
    const status = pill.dataset.status;

    if (status === 'epuise') return;
    if (status === 'bientot') {
      this.openNotify(pill.dataset.size);
      return;
    }

    this.selectVariant(pill);
  }

  /* ── Variante disponible ─────────────────────────────────────────────── */

  selectVariant(pill) {
    const variantId = pill.dataset.variantId;

    this.pills.forEach((p) => {
      if (p.dataset.status === 'disponible') {
        p.setAttribute('aria-pressed', String(p === pill));
      }
    });

    this.closeNotify();
    this.updateProductForm(variantId);
    this.updatePrice(pill.dataset.price);
    this.updateUrl(variantId);

    this.dispatchEvent(
      new CustomEvent('rv:variant:change', {
        bubbles: true,
        detail: { variantId, size: pill.dataset.size },
      })
    );
  }

  updateProductForm(variantId) {
    const form = document.querySelector(
      `product-form form[data-type="add-to-cart-form"], form[id^="product-form-"]`
    );
    if (!form) return;

    const input = form.querySelector('input[name="id"], select[name="id"]');
    if (input) {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Met à jour le prix affiché.
   *
   * Ce sélecteur remplace le variant-picker de Dawn, qui se chargeait
   * habituellement de rafraîchir le bloc prix. Le prix formaté est déjà porté
   * par la pilule (`data-price`), ce qui évite une requête réseau.
   */
  updatePrice(prixFormate) {
    if (!prixFormate) return;

    const bloc = document.getElementById(`price-${this.sectionId}`);
    if (!bloc) return;

    const cible = bloc.querySelector('.price-item--regular, .price-item--sale, .price__regular .price-item');
    if (cible) cible.textContent = prixFormate;
  }

  updateUrl(variantId) {
    if (!this.productUrl || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /* ── Panneau d'alerte ────────────────────────────────────────────────── */

  openNotify(size) {
    if (!this.notify) return;

    this.currentSize = size;

    if (this.notifyTitle && this.strings.notifyTitle) {
      this.notifyTitle.textContent = this.strings.notifyTitle.replace('__SIZE__', size);
    }

    // Réinitialise l'état si le panneau avait déjà servi pour une autre taille
    this.notifySuccess.hidden = true;
    this.notifyForm.hidden = false;
    this.clearError();

    this.notify.hidden = false;
    this.notify.setAttribute('aria-live', 'polite');

    // Focus au champ, après le rendu
    requestAnimationFrame(() => this.notifyInput?.focus());
  }

  closeNotify() {
    if (this.notify) this.notify.hidden = true;
  }

  /* ── Validation et envoi ─────────────────────────────────────────────── */

  validateEmail(showError) {
    const value = this.notifyInput.value.trim();

    if (value === '') {
      if (showError) this.showError(this.strings.errorEmpty);
      return false;
    }

    // Volontairement permissif : on rejette l'évidemment faux, pas l'inhabituel
    const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    if (!looksValid) {
      if (showError) this.showError(this.strings.errorInvalid);
      return false;
    }

    this.clearError();
    return true;
  }

  showError(message) {
    if (!this.notifyError) return;
    this.notifyError.textContent = message;
    this.notifyError.hidden = false;
    this.notifyInput.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    if (!this.notifyError) return;
    this.notifyError.hidden = true;
    this.notifyInput.removeAttribute('aria-invalid');
  }

  async onNotifySubmit(event) {
    event.preventDefault();

    if (!this.validateEmail(true)) {
      this.notifyInput.focus();
      return;
    }

    const button = this.notifyForm.querySelector('button[type="submit"]');
    button.setAttribute('aria-busy', 'true');

    const payload = {
      email: this.notifyInput.value.trim(),
      size: this.currentSize,
      productUrl: this.productUrl,
    };

    try {
      await this.submitNotification(payload);
      this.showSuccess();
    } catch (error) {
      console.error('[Révèle] Alerte de disponibilité :', error);
      this.showError(this.strings.errorInvalid);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Enregistrement de la demande d'alerte.
   *
   * Même mécanisme que l'alerte du Révélateur : évènement Klaviyo « Alerte sortie
   * Révèle » (voir `inscrireAlerte` dans revele-finder.js), avec la contenance en
   * propriété. L'événement DOM reste émis pour l'analytique.
   */
  async submitNotification(payload) {
    document.dispatchEvent(
      new CustomEvent('rv:notify:request', { bubbles: true, detail: payload })
    );

    if (typeof window.ReveleInscrireAlerte !== 'function') {
      throw new Error('inscription indisponible (Révélateur désactivé)');
    }
    const handle = (this.productUrl || location.pathname).split('/products/')[1]?.split(/[?#/]/)[0] ?? 'produit';
    await window.ReveleInscrireAlerte(payload.email, handle, {
      contenance: payload.size,
      source: 'fiche-produit',
      url: `${location.origin}/products/${handle}`,
    });
  }

  showSuccess() {
    const text = (this.strings.notifySuccess || '').replace('__SIZE__', this.currentSize);
    if (this.notifySuccessText) this.notifySuccessText.textContent = text;

    // Le formulaire disparaît : on ne s'inscrit pas deux fois à la même alerte
    this.notifyForm.hidden = true;
    this.notifySuccess.hidden = false;
  }
}

if (!customElements.get('rv-size-selector')) {
  customElements.define('rv-size-selector', RvSizeSelector);
}
